import * as THREE from "three";

/**
 * Where a tower's round leaves the model: a point fixed in the frame of an animated node, so it rides the `fire`
 * clip's recoil and pitch and the aim yaw `aimWeapon` adds, and lands on the barrel's mouth whatever way the tower or
 * the camera faces.
 *
 * The point is measured from the model's own geometry at rest rather than from a hand-typed table: the front face of
 * the named muzzle part (`bore`, `gun_tip`, `nozzle_tip`, the ballista's `bolt`, the frost `core`), or of the whole
 * `weapon` node when the part is missing. The art pass rebuilt every tower (25402da) and the hand-measured muzzle
 * table went stale with it; a measured point follows any rebuild.
 */
export interface Muzzle {
  /** The node the point rides (the muzzle part's parent, usually `<model>.weapon`). */
  readonly node: THREE.Object3D;
  /** The point in `node`'s local frame. */
  readonly local: THREE.Vector3;
}

/** One measured muzzle per model: which node it rides (by name) and where in that node's frame. */
interface MuzzleSpec { readonly node: string; readonly local: THREE.Vector3 }

/**
 * A part whose geometry reaches further forward than its origin by more than this (metres) is a long part — the
 * ballista's bolt, whose origin is its middle — and fires from its tip; a small marker fires from its centre.
 */
const TIP_REACH = 0.05;
/**
 * The barrel around a marker's axis: weapon vertices within this multiple of the marker's half-size (never less than
 * `AXIS_RADIUS_MIN` metres) of the axis count as the barrel when looking for its mouth.
 */
const AXIS_RADIUS_SCALE = 1.5;
const AXIS_RADIUS_MIN = 0.03;

const cache = new Map<string, MuzzleSpec | null>();

/** A node by the art's `<model>.<part>` name, as three.js's loader keeps it (the dot dropped) or not. */
function part(root: THREE.Object3D, model: string, name: string): THREE.Object3D | null {
  return root.getObjectByName(`${model}${name}`) ?? root.getObjectByName(`${model}.${name}`) ?? null;
}

const _m = new THREE.Matrix4(), _inv = new THREE.Matrix4(), _v = new THREE.Vector3(), _box = new THREE.Box3();

/**
 * The box of everything drawn under `node`, in `root`'s frame at the current pose; with `keep`, of only the vertices
 * it accepts. Plain part meshes are read directly; rigid rigs (rigidRig.ts: the parts merged into one skinned mesh
 * whose bones are the part nodes) through the vertices bound to the bones under `node`.
 */
export function partBox(root: THREE.Object3D, node: THREE.Object3D, out = new THREE.Box3(), keep: ((p: THREE.Vector3) => boolean) | null = null): THREE.Box3 {
  out.makeEmpty();
  root.updateMatrixWorld(true);
  _inv.copy(root.matrixWorld).invert();
  const under = new Set<THREE.Object3D>();
  node.traverse((o) => { under.add(o); });
  const add = (p: THREE.Vector3): void => { if (!keep || keep(p)) out.expandByPoint(p); };
  node.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || (mesh as THREE.SkinnedMesh).isSkinnedMesh) return;
    _m.multiplyMatrices(_inv, mesh.matrixWorld);
    if (!keep) {
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      out.union(_box.copy(mesh.geometry.boundingBox!).applyMatrix4(_m));
      return;
    }
    const pos = mesh.geometry.getAttribute("position");
    for (let i = 0; i < pos.count; i++) add(_v.fromBufferAttribute(pos, i).applyMatrix4(_m));
  });
  root.traverse((o) => {
    const skin = o as THREE.SkinnedMesh;
    if (!skin.isSkinnedMesh || !skin.skeleton) return;
    const bones = skin.skeleton.bones;
    const mine = new Set<number>();
    bones.forEach((b, i) => { if (under.has(b)) mine.add(i); });
    if (mine.size === 0) return;
    const pos = skin.geometry.getAttribute("position"), index = skin.geometry.getAttribute("skinIndex");
    if (!pos || !index) return;
    let current = -1;
    for (let i = 0; i < pos.count; i++) {
      const b = index.getX(i);
      if (!mine.has(b)) continue;
      if (b !== current) { current = b; _m.multiplyMatrices(_inv, bones[b].matrixWorld); }
      add(_v.fromBufferAttribute(pos, i).applyMatrix4(_m));
    }
  });
  return out;
}

/**
 * Measures the muzzle of `model` (a fresh clone at rest, named `name`); null when it has neither `muzzlePart` nor a
 * `weapon`. Cached per model name, so only the first clone of a model pays for the walk.
 */
export function measureMuzzle(model: THREE.Object3D, name: string, muzzlePart: string | null, atMarker = false): Muzzle | null {
  let spec = cache.get(name);
  if (spec === undefined) { spec = measure(model, name, muzzlePart, atMarker); cache.set(name, spec); }
  if (!spec) return null;
  const node = spec.node === "" ? model : model.getObjectByName(spec.node);
  return node ? { node, local: spec.local } : null;
}

/**
 * The barrel's mouth, in the model's frame. HQ models face −z in their own frame (`HQ_YAW` turns them, common.ts), so
 * "front" is the smallest z. A marker on the weapon (`bore`, `gun_tip` …) gives the barrel's axis: its centre, carried
 * forward to the front-most weapon geometry around that axis — the rebuilt tier-3 cannon's flared barrel runs 6 cm
 * past its `bore` disc. A long part (the ballista's loaded bolt, whose origin is its middle) fires from its tip; with
 * no marker, the weapon's front face does; an emitter off the weapon, from its centre.
 */
function measure(model: THREE.Object3D, name: string, muzzlePart: string | null, atMarker = false): MuzzleSpec | null {
  const marker = muzzlePart ? part(model, name, muzzlePart) : null;
  const weapon = part(model, name, "weapon");
  const source = marker ?? weapon;
  if (!source) return null;
  const box = partBox(model, source);
  const origin = source.getWorldPosition(new THREE.Vector3()).applyMatrix4(_inv.copy(model.matrixWorld).invert());
  const point = box.isEmpty() ? origin : box.getCenter(new THREE.Vector3());
  // `atMarker`: the marker IS the mouth (the Mortar's `bore` disc in a barrel pointing up, where "forward" is not −z)
  const aimed = !atMarker && !!marker && !!weapon && weapon !== marker && isAncestor(weapon, marker);
  // An emitter that doesn't aim (the frost core, the tesla crystal, the beacon orb) fires from its centre.
  if (!atMarker && !box.isEmpty() && (!marker || (aimed && Math.abs(box.min.z - origin.z) > TIP_REACH))) point.z = box.min.z;
  if (aimed && !box.isEmpty()) {
    const r = Math.max(AXIS_RADIUS_MIN, AXIS_RADIUS_SCALE * Math.max(box.max.x - box.min.x, box.max.y - box.min.y) / 2);
    const barrel = partBox(model, weapon, new THREE.Box3(), (p) => Math.hypot(p.x - point.x, p.y - point.y) <= r && p.z <= point.z);
    if (!barrel.isEmpty()) point.z = Math.min(point.z, barrel.min.z);
  }
  // Ride the marker's parent, not the marker: the clips scale markers (a muzzle flash, a bolt that vanishes and
  // re-cocks) while the parent carries the recoil and the aim.
  const node = marker?.parent ?? source;
  model.updateMatrixWorld(true);
  const toNode = _m.copy(node.matrixWorld).invert().multiply(model.matrixWorld);
  return { node: node === model ? "" : node.name, local: point.applyMatrix4(toNode) };
}

function isAncestor(a: THREE.Object3D, b: THREE.Object3D): boolean {
  for (let o = b.parent; o; o = o.parent) if (o === a) return true;
  return false;
}

/** World position of a muzzle at the node's current pose (call after the rig update and `aimWeapon`). */
export function muzzleWorld(m: Muzzle, out = new THREE.Vector3()): THREE.Vector3 {
  m.node.updateWorldMatrix(true, false);
  return out.copy(m.local).applyMatrix4(m.node.matrixWorld);
}

/** Forgets the measurements (tests; a hot-swapped art set). */
export function clearMuzzleCache(): void { cache.clear(); }
